import { useEffect } from 'react';
import ReactModal from 'react-modal';
import { FaTimes, FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Bind modal to app element for accessibility
// ReactModal.setAppElement('#root');

const Modal = ({ isOpen, onClose, title, content, image, links, technologies }) => {
  // Disable body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={title}
    >
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50"
              onClick={onClose}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="fixed inset-0 z-50 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                <div className="relative transform overflow-hidden rounded-lg bg-secondary text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
                  <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                    <button
                      type="button"
                      className="rounded-md bg-secondary/80 text-gray-400 hover:text-white focus:outline-none"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <FaTimes size={24} aria-hidden="true" />
                    </button>
                  </div>
                  
                  {image && (
                    <div className="w-full h-64 sm:h-72 md:h-80 relative">
                      <img
                        src={image}
                        alt={title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/20 to-transparent"></div>
                    </div>
                  )}
                  
                  <div className="px-4 pt-5 pb-4 sm:p-6">
                    <h3 className="text-2xl font-semibold text-white mb-2">{title}</h3>
                    
                    {technologies && technologies.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {technologies.map((tech, index) => (
                          <span 
                            key={index} 
                            className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="text-gray-300 my-4">
                      {content}
                    </div>
                    
                    {links && (
                      <div className="flex flex-wrap gap-4 mt-6">
                        {links.github && (
                          <a
                            href={links.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white bg-secondary hover:bg-secondary/80 border border-primary/30 px-4 py-2 rounded-lg transition-colors"
                          >
                            <FaGithub /> GitHub
                          </a>
                        )}
                        {links.live && (
                          <a
                            href={links.live}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white bg-primary hover:bg-primary/90 px-4 py-2 rounded-lg transition-colors"
                          >
                            <FaExternalLinkAlt /> Live Demo
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-secondary/80 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-secondary px-3 py-2 text-sm font-semibold text-white shadow-sm border border-gray-600 hover:bg-secondary/80 sm:mt-0 sm:w-auto"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </ReactModal>
  );
};

export default Modal; 